/**
 * RiskVisualizer — animated pipeline visualization for the risk engine.
 *
 * Mirrors GovernanceVisualizer but with risk-specific node metadata,
 * stage names, and labels. Reuses the same CSS animations.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ScanSearch, Layers, GitMerge, Filter, Scale, MessagesSquare,
  FolderOpen, SearchCheck, HelpCircle, BarChart3, Gauge, Check,
  type LucideIcon,
} from 'lucide-react';
import type { RiskStage, StepperNode, NodeStat } from '../../../hooks/useRiskSocket';
import styles from './GovernanceVisualizer.module.css';

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface Props {
  stage: RiskStage;
  stageMessage: string;
  stepperNodes: StepperNode[];
  areasProcessed: { area: string; cached: boolean }[];
  dimensionsExtracted: number;
  comparisonProgress: string;
  questionsCount: number;
  eventLog: string[];
  nodeStats: Record<string, NodeStat>;
}

/* ── Node metadata (risk-specific) ────────────────────────────────────────── */

interface NodeMeta {
  id: string;
  icon: LucideIcon;
  color: string;
  title: string;
  subtitle: string;
}

const NODE_META: Record<string, NodeMeta> = {
  risk_company_extraction:  { id: 'risk_company_extraction',  icon: ScanSearch,     color: 'var(--ds-link)', title: 'Scanning for risk controls',  subtitle: 'Extracting risk evidence from company documents' },
  risk_dimension_extraction:{ id: 'risk_dimension_extraction', icon: Layers,         color: 'var(--ds-link)', title: 'Extracting risk dimensions',   subtitle: 'Identifying risk requirements from policies' },
  risk_dimension_merge:     { id: 'risk_dimension_merge',      icon: GitMerge,       color: 'var(--ds-link)', title: 'Merging risk dimensions',      subtitle: 'Clustering semantically similar risk dimensions' },
  risk_priority_filter:     { id: 'risk_priority_filter',       icon: Filter,         color: 'var(--ds-link)', title: 'Filtering by risk priority', subtitle: 'Keeping critical & important risk dimensions' },
  risk_dimension_comparison:{ id: 'risk_dimension_comparison',  icon: Scale,          color: 'var(--ds-link)', title: 'Assessing risk controls',      subtitle: 'Evaluating controls against risk dimensions' },
  risk_question_merge:      { id: 'risk_question_merge',        icon: MessagesSquare, color: 'var(--ds-link)', title: 'Consolidating questions',      subtitle: 'Merging similar HITL questions' },
  risk_document_resolution: { id: 'risk_document_resolution',   icon: FolderOpen,     color: 'var(--ds-link)', title: 'Resolving via documents',      subtitle: 'Mapping questions to risk document types' },
  risk_re_extraction:       { id: 'risk_re_extraction',          icon: SearchCheck,    color: 'var(--ds-link)', title: 'Auto-extracting answers',      subtitle: 'Re-reading existing docs for missed evidence' },
  hitl:                     { id: 'hitl',                        icon: HelpCircle,     color: 'var(--ds-link)', title: 'Awaiting your input',          subtitle: 'Answer the questionnaire or upload documents' },
  risk_dimension_analysis:  { id: 'risk_dimension_analysis',    icon: BarChart3,      color: 'var(--ds-link)', title: 'Scoring risk dimensions',      subtitle: 'Computing risk scores with likelihood x impact' },
  risk_score_aggregation:   { id: 'risk_score_aggregation',     icon: Gauge,          color: 'var(--ds-success-text)', title: 'Calculating risk scores',      subtitle: 'Aggregating area risk scores into the final report' },
};

/* ── Particle field for background ─────────────────────────────────────────── */

function useParticles(count: number) {
  return useMemo(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 3,
      delay: Math.random() * 5,
      duration: 4 + Math.random() * 6,
    })),
  [count]);
}

/* ── Scene components (reused from governance, same CSS animations) ────────── */

function SceneExtraction() {
  return (
    <div className={styles.scene}>
      <div className={styles.docStack}>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className={styles.docPage} style={{ animationDelay: `${i * 0.35}s` }}>
            <div className={styles.docLine} /><div className={styles.docLine} />
            <div className={styles.docLine} style={{ width: '60%' }} />
          </div>
        ))}
      </div>
      <div className={styles.scanBeam} />
    </div>
  );
}

function SceneDimensionExtract() {
  // Neural-network fan-out: one policy neuron → many risk-dimension neurons.
  const inputCx = 18;
  const inputCy = 50;
  const outputs = useMemo(() => {
    const n = 9;
    return Array.from({ length: n }, (_, i) => {
      const t = i / (n - 1);
      const y = 14 + t * 72;
      const x = 78 + Math.sin(t * Math.PI) * 6;
      return { id: i, x, y, delay: 0.16 * i };
    });
  }, []);

  return (
    <div className={styles.scene}>
      <svg className={styles.neuralCanvas} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {outputs.map(o => (
          <line
            key={`s${o.id}`}
            className={styles.neuralSynapse}
            x1={inputCx} y1={inputCy} x2={o.x} y2={o.y}
            pathLength={1}
            style={{ animationDelay: `${o.delay}s` } as React.CSSProperties}
          />
        ))}
        {outputs.map(o => (
          <circle
            key={`i${o.id}`}
            className={styles.neuralImpulse}
            r={1.4}
            style={{
              offsetPath: `path('M ${inputCx} ${inputCy} L ${o.x} ${o.y}')`,
              animationDelay: `${o.delay + 0.05}s`,
            } as React.CSSProperties}
          />
        ))}
        <circle className={styles.neuralInput} cx={inputCx} cy={inputCy} r={6} />
        {outputs.map(o => (
          <circle
            key={`o${o.id}`}
            className={styles.neuralOutput}
            cx={o.x} cy={o.y} r={3.2}
            style={{ animationDelay: `${o.delay + 0.4}s` } as React.CSSProperties}
          />
        ))}
      </svg>
    </div>
  );
}

function SceneMerge() {
  const nodes = useMemo(() =>
    Array.from({ length: 16 }, (_, i) => ({
      id: i, startX: 15 + Math.random() * 70, startY: 10 + Math.random() * 80, cluster: i % 3,
    })),
  []);
  const clusterCenters = [{ x: 25, y: 50 }, { x: 50, y: 50 }, { x: 75, y: 50 }];
  return (
    <div className={styles.scene}>
      <svg className={styles.mergeCanvas} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {nodes.map(n => { const c = clusterCenters[n.cluster]; return (<line key={`l${n.id}`} className={styles.mergeLine} x1={n.startX} y1={n.startY} x2={c.x} y2={c.y} style={{ animationDelay: `${n.id * 0.12}s` }} />); })}
        {clusterCenters.map((c, i) => (<circle key={`c${i}`} className={styles.mergeCenter} cx={c.x} cy={c.y} r="5" style={{ animationDelay: `${1.2 + i * 0.2}s` }} />))}
        {nodes.map(n => { const dx = clusterCenters[n.cluster].x - n.startX; const dy = clusterCenters[n.cluster].y - n.startY; return (<circle key={`n${n.id}`} className={styles.mergeNode} cx={n.startX} cy={n.startY} r="2.5" style={{ '--dx': dx, '--dy': dy, animationDelay: `${n.id * 0.12}s` } as React.CSSProperties} />); })}
      </svg>
    </div>
  );
}

function SceneFilter() {
  return (
    <div className={styles.scene}>
      <div className={styles.filterFunnel}>
        <div className={styles.funnelTop}>
          {Array.from({ length: 10 }, (_, i) => (<div key={i} className={styles.filterBall} style={{ '--fall-x': `${-20 + Math.random() * 40}px`, animationDelay: `${i * 0.25}s`, background: i < 6 ? 'var(--ds-danger)' : 'var(--ds-border-strong)' } as React.CSSProperties} />))}
        </div>
        <div className={styles.funnelShape} />
        <div className={styles.funnelOutput}>
          {Array.from({ length: 6 }, (_, i) => (<div key={i} className={styles.filterBallOut} style={{ animationDelay: `${1.5 + i * 0.3}s` }} />))}
        </div>
      </div>
    </div>
  );
}

function SceneCompare() {
  return (
    <div className={styles.scene}>
      <div className={styles.compareContainer}>
        <div className={styles.compareColumn}>
          {[0, 1, 2, 3].map(i => (<div key={i} className={styles.compareBar} style={{ animationDelay: `${i * 0.3}s`, width: `${50 + Math.random() * 50}%` }} />))}
          <span className={styles.compareLabel}>Risk Req.</span>
        </div>
        <div className={styles.compareLinks}>
          {[0, 1, 2, 3].map(i => (<div key={i} className={styles.compareLink} style={{ animationDelay: `${0.6 + i * 0.3}s` }}><span className={styles.compareTick} /></div>))}
        </div>
        <div className={styles.compareColumn}>
          {[0, 1, 2, 3].map(i => (<div key={i} className={styles.compareBar} style={{ animationDelay: `${i * 0.3 + 0.15}s`, width: `${40 + Math.random() * 60}%` }} />))}
          <span className={styles.compareLabel}>Controls</span>
        </div>
      </div>
    </div>
  );
}

function SceneQuestionMerge() {
  return (
    <div className={styles.scene}>
      <div className={styles.qMergeContainer}>
        {[0, 1, 2, 3, 4].map(i => (<div key={i} className={styles.qBubble} style={{ animationDelay: `${i * 0.2}s` }}>?</div>))}
        <div className={styles.qMergeArrow} />
        <div className={styles.qMasterBubble}><span>1</span></div>
      </div>
    </div>
  );
}

function SceneAnalysis() {
  return (
    <div className={styles.scene}>
      <div className={styles.gearsContainer}>
        <div className={`${styles.gear} ${styles.gearLarge}`}>
          <svg viewBox="0 0 100 100"><path d="M50 10 L54 25 L60 12 L58 28 L66 18 L60 32 L70 24 L62 36 L76 32 L64 40 L80 40 L66 44 L82 50 L66 56 L80 60 L64 60 L76 68 L62 64 L70 76 L60 68 L66 82 L58 72 L60 88 L54 75 L50 90 L46 75 L40 88 L42 72 L34 82 L40 68 L30 76 L38 64 L24 68 L36 60 L20 60 L34 56 L18 50 L34 44 L20 40 L36 40 L24 32 L38 36 L30 24 L40 32 L34 18 L42 28 L40 12 L46 25 Z" fill="currentColor"/><circle cx="50" cy="50" r="12" fill="#fff"/></svg>
        </div>
        <div className={`${styles.gear} ${styles.gearSmall}`}>
          <svg viewBox="0 0 100 100"><path d="M50 15 L55 30 L65 18 L60 34 L72 28 L64 40 L78 38 L66 48 L80 50 L66 52 L78 62 L64 60 L72 72 L60 66 L65 82 L55 70 L50 85 L45 70 L35 82 L40 66 L28 72 L36 60 L22 62 L34 52 L20 50 L34 48 L22 38 L36 40 L28 28 L40 34 L35 18 L45 30 Z" fill="currentColor"/><circle cx="50" cy="50" r="10" fill="#fff"/></svg>
        </div>
      </div>
    </div>
  );
}

function SceneAggregation() {
  return (
    <div className={styles.scene}>
      <div className={styles.gaugeContainer}>
        <svg className={styles.gauge} viewBox="0 0 120 70">
          <path d="M10 65 A50 50 0 0 1 110 65" fill="none" stroke="var(--ds-border)" strokeWidth="8" strokeLinecap="round" />
          <path className={styles.gaugeFill} d="M10 65 A50 50 0 0 1 110 65" fill="none" stroke="var(--ds-danger)" strokeWidth="8" strokeLinecap="round"
            strokeDasharray="157" strokeDashoffset="157" />
          <text className={styles.gaugeText} x="60" y="58" textAnchor="middle">...</text>
        </svg>
        <div className={styles.gaugeParticles}>
          {Array.from({ length: 8 }, (_, i) => (<div key={i} className={styles.gaugeParticle} style={{ '--angle': `${180 + (i / 8) * 180}deg`, animationDelay: `${i * 0.15}s` } as React.CSSProperties} />))}
        </div>
      </div>
    </div>
  );
}

function SceneHitl() {
  return (
    <div className={styles.scene}>
      <div className={styles.hitlScene}>
        <div className={styles.hitlPulse} />
        <div className={styles.hitlIcon}>?</div>
      </div>
    </div>
  );
}

function SceneDocumentResolution() {
  return (
    <div className={styles.scene}>
      <div className={styles.docResolveContainer}>
        {[0, 1, 2].map(i => (<div key={i} className={styles.docResolveGroup} style={{ animationDelay: `${i * 0.5}s` }}><div className={styles.docResolveIcon}>{'\uD83D\uDCC4'}</div><div className={styles.docResolveBadge}>{2 + i}</div></div>))}
      </div>
    </div>
  );
}

function SceneReExtraction() {
  return (
    <div className={styles.scene}>
      <div className={styles.reExtractContainer}>
        <div className={styles.reExtractDoc}><div className={styles.docLine} /><div className={styles.docLine} /><div className={styles.docLine} style={{ width: '50%' }} /></div>
        <div className={styles.reExtractBeam} />
        <div className={styles.reExtractResults}>
          {[0, 1, 2].map(i => (<div key={i} className={styles.reExtractCheck} style={{ animationDelay: `${0.8 + i * 0.4}s` }}>{'\u2713'}</div>))}
        </div>
      </div>
    </div>
  );
}

const SCENE_MAP: Record<string, () => JSX.Element> = {
  risk_company_extraction: SceneExtraction,
  risk_dimension_extraction: SceneDimensionExtract,
  risk_dimension_merge: SceneMerge,
  risk_priority_filter: SceneFilter,
  risk_dimension_comparison: SceneCompare,
  risk_question_merge: SceneQuestionMerge,
  risk_document_resolution: SceneDocumentResolution,
  risk_re_extraction: SceneReExtraction,
  hitl: SceneHitl,
  risk_dimension_analysis: SceneAnalysis,
  risk_score_aggregation: SceneAggregation,
};

/* ── Main component ────────────────────────────────────────────────────────── */

export default function RiskVisualizer({
  stage, stageMessage, stepperNodes, areasProcessed,
  dimensionsExtracted, comparisonProgress,
  questionsCount, eventLog, nodeStats,
}: Props) {
  const particles = useParticles(20);
  const logEndRef = useRef<HTMLDivElement>(null);
  const [prevStage, setPrevStage] = useState(stage);

  useEffect(() => { logEndRef.current?.scrollIntoView(); }, [eventLog.length]);
  useEffect(() => { if (stage !== prevStage) setPrevStage(stage); }, [stage, prevStage]);

  const activeNode = stepperNodes.find(n => n.status === 'running');
  const activeId = activeNode?.id ?? 'risk_company_extraction';
  const meta = NODE_META[activeId] ?? NODE_META.risk_company_extraction;
  const SceneComponent = SCENE_MAP[activeId] ?? SceneExtraction;

  const liveProgress = useMemo((): { done: number; total: number; label: string } | null => {
    if (stage === 'risk_company_extraction') {
      return { done: areasProcessed.length, total: 10, label: 'risk areas' };
    }
    if (stage === 'risk_dimension_extraction' && dimensionsExtracted > 0) {
      return { done: dimensionsExtracted, total: 0, label: 'risk dimensions extracted' };
    }
    if (stage === 'risk_dimension_comparison' && comparisonProgress) {
      const parts = comparisonProgress.split('/');
      if (parts.length === 2) {
        return { done: parseInt(parts[0], 10) || 0, total: parseInt(parts[1], 10) || 0, label: 'dimensions assessed' };
      }
    }
    const completedStat = nodeStats[activeId];
    if (completedStat) {
      return { done: completedStat.done, total: completedStat.total, label: 'processed' };
    }
    return null;
  }, [stage, areasProcessed.length, dimensionsExtracted, comparisonProgress, nodeStats, activeId]);

  const completedNodes = useMemo(() =>
    stepperNodes
      .filter(n => n.status === 'complete' && nodeStats[n.id])
      .map(n => ({ id: n.id, meta: NODE_META[n.id], stat: nodeStats[n.id] })),
  [stepperNodes, nodeStats]);

  const pct = liveProgress && liveProgress.total > 0
    ? Math.min(100, Math.round((liveProgress.done / liveProgress.total) * 100))
    : null;

  return (
    <div className={styles.visualizer}>
      <div className={styles.particleField}>
        {particles.map(p => (
          <div key={p.id} className={styles.particle} style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s`, '--glow': meta.color } as React.CSSProperties} />
        ))}
      </div>

      <div className={styles.rail}>
        {stepperNodes
          .filter(node => !(node.id === 'hitl' && questionsCount === 0 && node.status === 'pending'))
          .map((node, idx, arr) => {
            const nm = NODE_META[node.id];
            return (
              <div key={node.id} className={`${styles.railNode} ${styles['rail_' + node.status]}`}>
                {idx > 0 && <div className={`${styles.railLine} ${styles['railLine_' + (arr[idx - 1]?.status === 'complete' ? 'done' : 'pending')]}`} />}
                <div className={styles.railDot}>{node.status === 'complete' ? <Check size={12} strokeWidth={2.5} /> : nm ? <nm.icon size={12} strokeWidth={2} /> : <span className={styles.railDotBullet} />}</div>
                <span className={styles.railLabel}>{nm?.title ?? node.label}</span>
              </div>
            );
          })}
      </div>

      <div className={styles.heroCenter} style={{ '--accent': meta.color } as React.CSSProperties}>
        <div className={styles.heroAnimation} key={activeId}><SceneComponent /></div>
        <div className={styles.heroInfo}>
          <span className={styles.heroIcon}><meta.icon size={30} strokeWidth={1.6} /></span>
          <h2 className={styles.heroTitle}>{meta.title}</h2>
          <p className={styles.heroSubtitle}>{stageMessage || meta.subtitle}</p>
          {liveProgress && (
            <div className={styles.progressSection}>
              <div className={styles.progressBarTrack}>
                <div className={styles.progressBarFill} style={{ width: pct != null ? `${pct}%` : '100%', background: pct != null ? meta.color : undefined }} />
              </div>
              <div className={styles.progressNumbers}>
                <span className={styles.progressCount}>{liveProgress.done}</span>
                {liveProgress.total > 0 && <span className={styles.progressTotal}>/ {liveProgress.total}</span>}
                <span className={styles.progressLabel}>{liveProgress.label}</span>
                {pct != null && <span className={styles.progressPct}>{pct}%</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      {completedNodes.length > 0 && (
        <div className={styles.counterRow}>
          {completedNodes.map(({ id, meta: nm, stat }) => {
            const extra = stat.extra;
            const displayLabel = extra ? Object.entries(extra).map(([k, v]) => `${v} ${k}`).join(' \u2192 ') : `${stat.done} / ${stat.total}`;
            return (
              <div key={id} className={styles.counterChip}>
                <span className={styles.counterIcon}>{nm ? <nm.icon size={14} strokeWidth={2} /> : <Check size={14} strokeWidth={2.5} />}</span>
                <span className={styles.counterValue}>{displayLabel}</span>
                <span className={styles.counterLabel}>{nm?.title ?? id}</span>
              </div>
            );
          })}
        </div>
      )}

      {eventLog.length > 0 && (
        <div className={styles.feed}>
          <div className={styles.feedHeader}><div className={styles.feedDot} /><span>Live feed</span></div>
          <div className={styles.feedScroll}>
            {eventLog.map((msg, i) => (<div key={i} className={styles.feedLine}><span className={styles.feedBullet} />{msg}</div>))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}
    </div>
  );
}
